import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildMarketClosuresColumns } from '../columns';
import {
  CLOSURE_SORT_KEYS,
  type Closure,
  type ClosureSortKey,
  type SortDirection,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<Closure> | undefined;
  onEdit: (row: Closure) => void;
  onDelete: (row: Closure) => void;
  onSortChange: (sort: { sortBy?: ClosureSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(CLOSURE_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: ClosureSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as ClosureSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function MarketClosuresGrid({
  data,
  onEdit,
  onDelete,
  onSortChange,
}: Props) {
  const columnDefs = useMemo(
    () => buildMarketClosuresColumns({ onEdit, onDelete }),
    [onEdit, onDelete],
  );

  return (
    <div className="h-full w-full">
      <AgGridReact<Closure>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
